/** Libraries */
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as uuid from "uuid";
import IconButton from "../IconButton";
import DrawToSign from "./draw-to-sign";
import TypeToSign from "./type-to-sign";
import { FormInput } from "./shared-styled-components";
import { colorAttentionBlue10, colorAttentionBlue50, colorAttentionRed40, colorAttentionRed50, colorFunctionalGray20, colorFunctionalGray30, colorFunctionalGray60, colorFunctionalGray70, colorFunctionalWhite, fontSizeS, IconSignature, IconText, IconX, spacingS, } from "@rhumbix/rmbx_design_system_design_tokens";
const TYPE = "type";
const DRAW = "draw";
export const signatureStyles = {
    stageHeight: 190,
    iconSize: 16,
};
const HybridSignaturePad = (props) => {
    // Create a signature box and pass that into either signature type
    const signatureBox = useRef(null);
    const nameInput = useRef(null);
    const { updateSignature, clearSigFromStore, sigName, stageHeight = signatureStyles.stageHeight, hasMixedValues, hideDrawNameInput, forceMode, } = props;
    const name = sigName ?? "";
    const sigHtmlName = `sigName-${uuid.v4()}`;
    // we underlay an existing signature if there is one, so default to draw so we don't have the type overlay
    const [padType, setPadType] = useState(forceMode ?? TYPE);
    const [lines, setLines] = React.useState([]);
    const [signatureName, setSignatureName] = useState(name);
    // If a user is in Draw mode and types their name, then switches to
    // Type mode, we want to not only draw that name in the box, but also
    // store it as the signature.
    useEffect(() => {
        if (signatureName) {
            addSig();
            // this allows them to keep typing after switching without clicking on the input again
            // also triggers the red highlight to encourage drawing a sig on the DRAW pad
            nameInput.current?.focus();
        }
    }, [padType]);
    useEffect(() => {
        // This covers if the name input is filled via browser autofill
        addSig();
    }, [signatureName]);
    const addSig = () => {
        const sigInfo = getSigInfo();
        updateSignature(sigInfo.base64Img, sigInfo.name);
    };
    const getSigInfo = () => {
        const sigDrawn = (padType === DRAW && lines.length) || (padType === TYPE && signatureName);
        const sigImg = signatureBox.current;
        const imgOptions = {
            mimeType: "image/png",
            quality: 0,
            pixelRatio: 2,
        };
        return {
            base64Img: sigDrawn && sigImg ? sigImg.toDataURL(imgOptions) : "",
            name: signatureName ?? "",
        };
    };
    const onTextChange = (event) => setSignatureName(event.target.value);
    const resetSignature = (clearSignatureName = false) => {
        if (clearSignatureName)
            setSignatureName("");
        setLines([]);
        clearSigFromStore();
    };
    return (React.createElement("div", null,
        React.createElement(SignatureBlock, { hasMixedValues: hasMixedValues, isPartialSig: !!signatureName && padType === DRAW && !lines.length },
            React.createElement("div", null,
                React.createElement(SignatureButtonBar, null,
                    React.createElement(SignatureButton, null,
                        !forceMode && (React.createElement(IconButton, { text: "Type", type: "button", onClick: () => {
                                resetSignature(false);
                                setPadType(TYPE);
                            }, style: { backgroundColor: "transparent" }, className: `btn-small sig-btn ${padType === TYPE ? "sig-type-selected" : ""}`, icon: color => React.createElement(IconText, { height: signatureStyles.iconSize, color: color }) })),
                        !forceMode && (React.createElement(IconButton, { text: "Draw", type: "button", style: { backgroundColor: "transparent" }, onClick: () => {
                                resetSignature(false);
                                setPadType(DRAW);
                            }, className: `btn-small sig-btn ${padType === DRAW ? "sig-type-selected" : ""}`, icon: color => (React.createElement(IconSignature, { height: signatureStyles.iconSize, color: color })) }))),
                    React.createElement(SignatureButton, null,
                        React.createElement(IconButton, { text: "Clear Signature", type: "button", style: { backgroundColor: "transparent" }, onClick: () => resetSignature(true), className: "sig-btn clear-signature", 
                            // override the theme colors and force it to be red
                            icon: __ => React.createElement(IconX, { color: colorAttentionRed50, height: signatureStyles.iconSize }) }))),
                padType === TYPE ? (React.createElement(TypeToSign, { signatureBox: signatureBox, stageHeight: stageHeight, name: signatureName, addSig: addSig, onTextChange: onTextChange, sigHtmlName: sigHtmlName, hasMixedValues: hasMixedValues, inputRef: nameInput })) : (React.createElement(DrawToSign, { signatureBox: signatureBox, stageHeight: stageHeight, lines: lines, setLines: setLines, addSig: addSig })))),
        padType === DRAW && !hideDrawNameInput && (
        // type-to-sign has a text input within it, so only need for drawing version
        React.createElement(SigNameInput, null,
            React.createElement(FieldLabel, null, "Full Name"),
            React.createElement(RequiredLabel, { hasValue: !!signatureName.length }, "REQUIRED"),
            React.createElement(FormInput, { ref: nameInput, className: `sig-name-input form-input${lines.length && !signatureName ? " partial-sig" : ""}`, value: signatureName, name: sigHtmlName, onChange: onTextChange, placeholder: "Enter full name", onBlur: addSig })))));
};
export default HybridSignaturePad;
const SignatureBlock = styled.div `
    border: 2px solid ${colorFunctionalGray30};
    border-radius: 5px;
    background-color: ${props => (props.hasMixedValues ? colorAttentionBlue10 : colorFunctionalWhite)};
    min-height: 100px;
    margin-bottom: ${spacingS};
    &:hover,
    &:focus,
    &:focus-visible,
    &:focus-within {
        border: 2px solid ${colorAttentionBlue50};
    }
    ${({ isPartialSig }) => isPartialSig && {
    border: `2px solid ${colorAttentionRed50} !important`,
}}
`;
const SignatureButtonBar = styled.div `
    margin-left: 24px;
    display: flex;
    justify-content: space-between;
`;
const SignatureButton = styled.div `
    float: left;
    color: ${colorFunctionalGray70};
    display: flex;
    flex-direction: row;
    position: relative;
    > button {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        width: 80px;
        margin: ${spacingS};
        font-size: ${fontSizeS};
        font-weight: 400;
        margin-left: -16px;
        border: 0;
        background: none;
        padding: 0;
        min-height: 40px;
    }
    & .sig-type-selected {
        background-color: ${colorFunctionalGray20} !important;
        & .content {
            & .text {
                font-weight: 700;
            }
        }
    }
    & .spacer {
        width: 5px;
    }
    & .clear-signature {
        float: right;
        width: 130px;
    }
`;
const SigNameInput = styled.div `
    display: flex;
    margin-left: ${spacingS};
    align-items: baseline;
`;
const FieldLabel = styled.label `
    font-size: 12px;
    width: 60px;
    min-width: 60px;
`;
const RequiredLabel = styled.div `
    font-size: ${spacingS};
    margin-left: ${spacingS};
    margin-right: ${spacingS};
    color: ${props => (props.hasValue ? colorFunctionalGray60 : colorAttentionRed40)};
`;
